import React, { useCallback, useMemo, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { debounce } from "lodash";
import SimpleColorPicker from "util/components/color-picker";
import * as metrics from "util/metrics";
import ModalButtonGroup from "visual-components/util/buttons/ModalButtonGroup";

const track = debounce((data) => {
  metrics.create("campaignManagerUpdatedScrollButtonAttribute", {
    meta: data,
  });
}, 500);

/**
 * @param {Object} props - Component props
 * @param {Object} props.elements - Template elements configuration
 * @param {Function} props.updateAttr - Function to update element attributes
 * @param {Function} props.onSave - Callback when save is clicked
 * @param {Function} props.onCancel - Callback when cancel is clicked
 * @returns {import("react").ReactElement}
 */
export default function ScrollButtonEditor({ elements, updateAttr, onSave, onCancel }) {
  // Read from styles (persisted) with fallback to top-level (legacy) then default
  const showScrollButton = elements.styles?.showScrollButton ?? elements.showScrollButton ?? true;
  const initialText =
    elements.styles?.scrollButtonText ?? elements.scrollButtonText ?? "ENTER TO WIN";
  const [buttonText, setButtonText] = useState(initialText);

  const updateStylesAttr = useCallback(
    (attr, value) => {
      const newStyles = {
        ...elements.styles,
        [attr]: value,
      };
      updateAttr("styles", newStyles, true);
    },
    [elements.styles, updateAttr],
  );

  const debouncedUpdateStyles = useMemo(
    () =>
      debounce((value) => {
        track({ type: "scrollButton", attr: "scrollButtonText" });
        updateStylesAttr("scrollButtonText", value);
      }, 300),
    [updateStylesAttr],
  );

  const handleTextChange = useCallback(
    (e) => {
      const value = e.target.value;
      setButtonText(value);
      debouncedUpdateStyles(value);
    },
    [debouncedUpdateStyles],
  );

  const toggleShowScrollButton = useCallback(() => {
    const newValue = !showScrollButton;
    track({ type: "scrollButton", attr: "showScrollButton", value: newValue });
    updateStylesAttr("showScrollButton", newValue);
  }, [showScrollButton, updateStylesAttr]);

  return (
    <div className="bg-white p-6">
      <h3 className="mb-6 text-center text-lg font-semibold">Edit Hero Button</h3>

      <FormControlLabel
        control={
          <Checkbox
            checked={showScrollButton}
            onChange={toggleShowScrollButton}
            size="small"
          />
        }
        label="Show scroll button in hero section"
        slotProps={{ typography: { fontSize: 14 } }}
      />

      {showScrollButton && (
        <>
          <div className="mb-4">
            <h6 className="mb-2 text-sm font-medium">Button Text</h6>
            <input
              type="text"
              value={buttonText}
              onChange={handleTextChange}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Enter button text"
            />
          </div>

          <hr className="my-4 border-gray-300" />

          <div className="mb-4">
            <h6 className="mb-2 text-sm font-medium">Colors</h6>
            <div className="mt-2 flex gap-4">
              <SimpleColorPicker
                attr="scrollButtonColor"
                updateAttr={updateAttr}
                model={elements.styles?.fonts ?? {}}
                label="Button Color"
              />
              <SimpleColorPicker
                attr="scrollButtonTextColor"
                updateAttr={updateAttr}
                model={elements.styles?.fonts ?? {}}
                label="Text Color"
              />
            </div>
          </div>
        </>
      )}

      <hr className="my-4 border-gray-300" />

      <ModalButtonGroup
        confirm={onSave}
        confirmText="Save"
        cancel={onCancel}
        cancelText="Cancel"
        canSave
        hideLine
      />
    </div>
  );
}
